import { useRecoilState } from "recoil";
import {
  currentSessionAtom,
  isSessionErrored,
  isSessionActive,
  pollingSessionAtom
} from "../state";
import { useSessionApiClient } from "../api/sessions";
import { useEffect, useState } from "react";
import {
  INITIAL_SESSION_CREATED_WAIT_MS,
  POLL_SESSION_CREATED_INTERVAL_MS,
} from "../constants";
import CircularProgressWithLabel from "./CircularProgressWithLabel";

const shouldWaitForSession = (currentSession) => {
  return (
    currentSession?.id &&
    !isSessionActive(currentSession) &&
    !isSessionErrored(currentSession)
  );
};

const SessionLoader = (props) => {
  const apiSessionClient = useSessionApiClient();
  const [pollingSession, setPollingSession] = useRecoilState(pollingSessionAtom);
  const [currentSession, setCurrentSession] =
    useRecoilState(currentSessionAtom);
  const [sessionLoaderPercent, setSessionLoaderPercent] =
    useState(0);

  const pollSession = (id, interval) => {
    apiSessionClient.getSession(id).then((session) => {
      setSessionLoaderPercent(session.initProgress);
      if (session.status !== "pending") {
        setCurrentSession(session);
        setPollingSession(false);
        clearInterval(interval);
      }
    });
  };

  const getCreatedSession = (id) => {
    const interval = setInterval(async () => {
      try {
        pollSession(id, interval);
      } catch (err) {
        // error catch needed in case of any issues with .getSession()
        setPollingSession(false);
        clearInterval(interval);
      }
    }, POLL_SESSION_CREATED_INTERVAL_MS);

    // for small sessions, an immediate check is too quick (it will still be pending)
    // and a 5 seconds wait is too long. So this timeout has a short initial wait
    // for sessions that fall in a 0-INITIAL_SESSION_CREATED_WAIT_MS creation window
    setTimeout(
      async () => pollSession(id, interval),
      INITIAL_SESSION_CREATED_WAIT_MS
    );

    return interval;
  };

  useEffect(() => {
    let interval = null;
    if (shouldWaitForSession(currentSession) && !pollingSession) {
      setPollingSession(true);
      interval = getCreatedSession(currentSession.id);
    }

    return () => {
      setPollingSession(false);
      clearInterval(interval);
    };
  }, [currentSession]);

  if (shouldWaitForSession(currentSession)) {
    return <CircularProgressWithLabel value={sessionLoaderPercent} label={"Loading Session..."}/>;
  } else if (isSessionErrored(currentSession)) {
    throw new Error(
      `Failed to load session.<br/> Details: ${currentSession.statusMessage}`
    );
  } else {
    return <>{props.children}</>;
  }
};

export default SessionLoader;
